import {Button, Dialog, Hotkey, IconButton, Switch} from '@wandb/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
import {useCallback, useMemo, useRef} from 'react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import type * as FilterTypes from '../../../util/filterTypes';
import {
  activeFilterCountFromStored,
  type FilterV2Root,
  getFiltersForUI,
  isFilterV2Group,
  type StoredFilter,
  upsertToggleFilter,
} from '../../../util/filterV2';
import type * as RunTypes from '../../../util/runTypes';
import {maybePluralize} from '../../../util/uihelpers';
import {useTrackFilterWithLocation} from '../../Filters/filterEvents';
import {type FilterKeySelectorCreatorProps} from '../../Filters/FilterKeySelector';
import FilterListV2 from '../../Filters/FilterListV2';
import {type FilterValueSelectorCreatorProps} from '../../Filters/FilterValueSelector';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';

interface WBTableActionFilterButtonProps {
  compact?: boolean;
  filtersOpen: boolean;
  filters: StoredFilter;
  extraFilterCount?: number;
}

export const WBTableActionFilterButton = ({
  compact,
  filters,
  extraFilterCount,
  filtersOpen,
}: WBTableActionFilterButtonProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const filterCount =
    activeFilterCountFromStored(filters) + (extraFilterCount ?? 0);
  const buttonText =
    filterCount > 0 ? maybePluralize(filterCount, 'filter') : 'Filter';

  const isActive = filtersOpen || filterCount > 0;
  // Dialog.Trigger owns opening and closing the popover, so this only reports.
  const onPress = () => {
    window.analytics?.track('Filter dropdown clicked', {
      location: analyticsLocation,
    });
  };

  return compact ? (
    <IconButton
      tooltip={{children: 'Filter'}}
      aria-label="Filter"
      isActive={isActive}
      variant="secondary"
      icon="filter-alt"
      data-test="filter-button"
      onPress={onPress}
    />
  ) : (
    <Button
      isActive={isActive}
      variant="secondary"
      startIcon="filter-alt"
      data-test="filter-button"
      aria-label={buttonText}
      onPress={onPress}>
      {buttonText}
    </Button>
  );
};

interface WBTableActionFilterPickerProps {
  filters: StoredFilter;

  defaultToggleFilters?: FilterTypes.DefaultToggleFilter[];

  hideManuallyHidden?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };

  headerContent?: React.ReactNode;

  setFilters(filters: StoredFilter): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;

  filterValueSelector(
    props: FilterValueSelectorCreatorProps<RunTypes.Key>
  ): React.ReactNode;
}

export const WBTableActionFilterPicker = (
  props: WBTableActionFilterPickerProps
) => {
  return (
    <div className="filter-popup">
      <FilterPickerContent {...props} />
    </div>
  );
};

const FilterPickerContent = (props: WBTableActionFilterPickerProps) => {
  const {filters, defaultToggleFilters, hideManuallyHidden} = props;
  const {trackGroupCreated, trackORToggled} = useTrackFilterWithLocation();
  const v2Root = useMemo(() => getFiltersForUI(filters), [filters]);

  const handleChange = useCallback(
    (newRoot: FilterV2Root) => {
      props.setFilters(newRoot);
    },
    [props]
  );

  const handleToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) => {
      const updated = upsertToggleFilter(v2Root, toggleFilter);
      props.setFilters(updated);
    },
    [v2Root, props]
  );

  const hasEnabledToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) =>
      v2Root.filters.some(
        item =>
          !isFilterV2Group(item) &&
          !item.disabled &&
          item.key.section === toggleFilter.key.section &&
          item.key.name === toggleFilter.key.name &&
          item.op === toggleFilter.op &&
          JSON.stringify(item.value) === JSON.stringify(toggleFilter.value)
      ),
    [v2Root]
  );

  return (
    <>
      {props.headerContent}
      <FilterListV2
        root={v2Root}
        onChange={handleChange}
        onGroupCreated={trackGroupCreated}
        onORToggled={trackORToggled}
        filterKeySelector={props.filterKeySelector}
        filterValueSelector={
          props.filterValueSelector as unknown as (
            p: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
          ) => React.ReactNode
        }
      />
      {(defaultToggleFilters || hideManuallyHidden) && (
        <WBTableActionFilterPickerDefaultToggles
          defaultToggleFilters={defaultToggleFilters ?? []}
          onToggle={handleToggle}
          hasEnabled={hasEnabledToggle}
          hideManuallyHidden={hideManuallyHidden}
          showDivider
        />
      )}
    </>
  );
};

const WBTableActionFilterPickerDefaultToggles = ({
  defaultToggleFilters,
  onToggle,
  hasEnabled,
  hideManuallyHidden,
  showDivider,
}: {
  defaultToggleFilters: FilterTypes.DefaultToggleFilter[];
  onToggle: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
  hasEnabled: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => boolean;
  hideManuallyHidden?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };
  showDivider?: boolean;
}) => {
  const isInReport = useIsInLoadedReportContext();
  return (
    <Tailwind>
      {showDivider && <hr className="my-16 border-wb-border" />}
      <div className="flex flex-col gap-8">
        {defaultToggleFilters.map((defaultFilter, i) => (
          <Switch
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={i}
            isSelected={hasEnabled(defaultFilter.filter)}
            onChange={() => onToggle(defaultFilter.filter)}>
            {defaultFilter.label}
          </Switch>
        ))}
        {hideManuallyHidden && (
          <Switch
            isSelected={hideManuallyHidden.checked}
            onChange={hideManuallyHidden.onChange}>
            <span className="flex items-center gap-8">
              List only visible runs
              {/* Hotkey's own hidden text repeats the switch's name, so the
                  chips are visual only and the shortcut joins the name once.
                  The library's Switch does not accept aria-describedby, which
                  would be the place for it. */}
              {!isInReport && (
                <>
                  <span aria-hidden>
                    <Hotkey
                      keys={['Alt', 'KeyV']}
                      name="List only visible runs"
                    />
                  </span>
                  <span className="sr-only">(Alt + V)</span>
                </>
              )}
            </span>
          </Switch>
        )}
      </div>
    </Tailwind>
  );
};

interface WBTableActionFilterProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trigger: (isOpen: boolean) => React.ReactNode;
  content: React.ReactNode;
}

/** Spread onto the real <button> that opens a WBTableActionFilterWithPlainTrigger. */
export type PlainTriggerProps = {
  ref: React.Ref<HTMLButtonElement>;
  onClick: () => void;
  'aria-expanded': boolean;
  'aria-haspopup': 'dialog';
};

interface WBTableActionFilterWithPlainTriggerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trigger: (
    isOpen: boolean,
    triggerProps: PlainTriggerProps
  ) => React.ReactNode;
  content: React.ReactNode;
}

const FilterPopoverBody = ({
  content,
  onClose,
}: {
  content: React.ReactNode;
  onClose: () => void;
}) => (
  <Tailwind>
    {/* Portal-rendered, so it doesn't inherit the run table's allow; filter
        keys/values shown here are already exposed in the table itself.
        Dialog.Popover sizes to min-content and caps itself at 92vw, so the
        body asks for its max-content width and, once the cap bites, shrinks
        with the popover instead of scrolling inside it. */}
    <div className="w-max max-w-full" data-dd-privacy={DD_PRIVACY_ALLOW}>
      <div className="flex items-center justify-between mb-10">
        <Dialog.Heading mb={0}>Filters</Dialog.Heading>
        <IconButton
          size="md"
          variant="ghost"
          icon="close"
          aria-label="Close filters"
          onPress={onClose}
        />
      </div>
      {content}
    </div>
  </Tailwind>
);

// Approximates the `.833em 1em` padding the semantic-ui popup used to inherit.
const POPOVER_PADDING = {pt: 5, pb: 5, pl: 6, pr: 6} as const;

// Both variants render the same popover; the semantic-ui popups they replace
// were both anchored bottom-left.
const POPOVER_PLACEMENT = 'bottom start';

export const WBTableActionFilter = ({
  open,
  setOpen,
  trigger,
  content,
}: WBTableActionFilterProps) => (
  <Dialog.Trigger isOpen={open} onOpenChange={setOpen}>
    {trigger(open)}
    <Dialog.Popover placement={POPOVER_PLACEMENT} {...POPOVER_PADDING}>
      <FilterPopoverBody content={content} onClose={() => setOpen(false)} />
    </Dialog.Popover>
  </Dialog.Trigger>
);

/**
 * Dialog.Trigger hands press behaviour to its first child through react-aria
 * context, which only a library button consumes: the vendored library bundles
 * its own react-aria, so the app's `Pressable` cannot join that context. The
 * custom charts editor supplies its own styled button, so this variant follows
 * the library's controlled pattern instead: one open state, a Dialog.Popover
 * anchored to the button through `triggerRef`, and the button wired up by
 * spreading `triggerProps`.
 *
 * Pressing the button while the popover is open closes it the same way it
 * does for a library trigger: the popover is modal and covers the page with
 * an underlay, so the press lands on the underlay and dismisses the popover
 * before any click can reach the button.
 */
export const WBTableActionFilterWithPlainTrigger = ({
  open,
  setOpen,
  trigger,
  content,
}: WBTableActionFilterWithPlainTriggerProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      {trigger(open, {
        ref: triggerRef,
        onClick: () => setOpen(!open),
        'aria-expanded': open,
        'aria-haspopup': 'dialog',
      })}
      <Dialog.Popover
        isOpen={open}
        onOpenChange={setOpen}
        triggerRef={triggerRef}
        placement={POPOVER_PLACEMENT}
        {...POPOVER_PADDING}>
        <FilterPopoverBody content={content} onClose={() => setOpen(false)} />
      </Dialog.Popover>
    </>
  );
};
